/** Maps the fixed-vocabulary codes (also used by the bulk upload/download template) to
 * productManagement translation keys, so the UI dropdowns show a translated label while the
 * stored/uploaded value stays the raw code. Values with no mapping (e.g. DB-driven UOM codes)
 * fall back to displaying the raw code as-is. */

export const ALLERGEN_STATUS_LABEL_KEYS: Record<string, string> = {
  CONTAINS: 'allergenStatusContains',
  MAY_CONTAIN: 'allergenStatusMayContain',
  NOT_PRESENT: 'allergenStatusNotPresent',
  FREE_FROM: 'allergenStatusFreeFrom',
  UNKNOWN: 'allergenStatusUnknown',
};

/** Translation keys for the "what does this status mean" info box next to the AllergenStatus dropdown. */
export const ALLERGEN_STATUS_MEANING_KEYS: Record<string, string> = {
  CONTAINS: 'allergenStatusMeaningContains',
  MAY_CONTAIN: 'allergenStatusMeaningMayContain',
  NOT_PRESENT: 'allergenStatusMeaningNotPresent',
  FREE_FROM: 'allergenStatusMeaningFreeFrom',
  UNKNOWN: 'allergenStatusMeaningUnknown',
};

export const ASSESSMENT_SOURCE_TYPE_LABEL_KEYS: Record<string, string> = {
  SUPPLIER_SPECIFICATION: 'sourceTypeSupplierSpecification',
  MANUFACTURER_SPECIFICATION: 'sourceTypeManufacturerSpecification',
  PRODUCT_LABEL: 'sourceTypeProductLabel',
  LABORATORY_ANALYSIS: 'sourceTypeLaboratoryAnalysis',
  CERTIFICATE: 'sourceTypeCertificate',
  INGREDIENT_ASSESSMENT: 'sourceTypeIngredientAssessment',
  RECIPE_ASSESSMENT: 'sourceTypeRecipeAssessment',
  CUSTOMER_DECLARATION: 'sourceTypeCustomerDeclaration',
  MANUAL_ASSESSMENT: 'sourceTypeManualAssessment',
};

export const VALUE_QUALIFIER_LABEL_KEYS: Record<string, string> = {
  EXACT: 'valueQualifierExact',
  APPROXIMATE: 'valueQualifierApproximate',
  LESS_THAN: 'valueQualifierLessThan',
  LESS_THAN_OR_EQUAL: 'valueQualifierLessThanOrEqual',
  GREATER_THAN: 'valueQualifierGreaterThan',
  GREATER_THAN_OR_EQUAL: 'valueQualifierGreaterThanOrEqual',
  TRACE: 'valueQualifierTrace',
  NOT_DETECTED: 'valueQualifierNotDetected',
  UNKNOWN: 'valueQualifierUnknown',
};

export const NUTRITION_BASIS_TYPE_LABEL_KEYS: Record<string, string> = {
  PER_100_G: 'basisTypePer100G',
  PER_100_ML: 'basisTypePer100Ml',
  PER_SERVING: 'basisTypePerServing',
  PER_PORTION: 'basisTypePerPortion',
  PER_UNIT: 'basisTypePerUnit',
  PER_PACK: 'basisTypePerPack',
};

export const NUTRITION_PREPARATION_STATE_LABEL_KEYS: Record<string, string> = {
  AS_SOLD: 'preparationStateAsSold',
  AS_PREPARED: 'preparationStateAsPrepared',
  AS_SERVED: 'preparationStateAsServed',
  AS_CONSUMED: 'preparationStateAsConsumed',
};

export const ANALYSIS_METHOD_LABEL_KEYS: Record<string, string> = {
  LABORATORY_ANALYSIS: 'analysisMethodLaboratoryAnalysis',
  CALCULATED_FROM_INGREDIENTS: 'analysisMethodCalculatedFromIngredients',
  CALCULATED_FROM_RECIPE: 'analysisMethodCalculatedFromRecipe',
  SUPPLIER_DECLARED: 'analysisMethodSupplierDeclared',
  MANUFACTURER_DECLARED: 'analysisMethodManufacturerDeclared',
  REFERENCE_DATABASE: 'analysisMethodReferenceDatabase',
  LABEL_TRANSCRIPTION: 'analysisMethodLabelTranscription',
  ESTIMATED: 'analysisMethodEstimated',
  UNKNOWN: 'analysisMethodUnknown',
};

export const DIETARY_ATTRIBUTE_CATEGORY_LABEL_KEYS: Record<string, string> = {
  DIETARY_REQUIREMENT: 'dietaryAttributeCategoryDietaryRequirement',
  LIFESTYLE_PREFERENCE: 'dietaryAttributeCategoryLifestylePreference',
  INTOLERANCE: 'dietaryAttributeCategoryIntolerance',
  NUTRITIONAL_CLAIM: 'dietaryAttributeCategoryNutritionalClaim',
};

export const DIETARY_ATTRIBUTE_STATUS_LABEL_KEYS: Record<string, string> = {
  SUITABLE: 'dietaryAttributeStatusSuitable',
  NOT_SUITABLE: 'dietaryAttributeStatusNotSuitable',
  CERTIFIED: 'dietaryAttributeStatusCertified',
  UNKNOWN: 'dietaryAttributeStatusUnknown',
};

/** Looks up the translated label for a fixed-vocabulary code; falls back to the raw code (e.g. DB-driven UOM codes have no mapping). */
export const getEnumLabel = (
  t: (key: string, fallback: string) => string,
  labelKeys: Record<string, string>,
  value: string,
): string => {
  const key = labelKeys[value?.toUpperCase()];
  return key ? t(key, value) : value;
};
